import React, { useEffect } from 'react';
import { ListGroup, Alert } from 'react-bootstrap';
import { useQuery } from 'react-query';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorAlert from '../common/ErrorAlert';
import { ConversationListItem, getConversations } from '../../services/messageService';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../common/ToastContainer';

// TODO: Fetch conversations using useQuery and messageService.getConversations
// TODO: Implement ConversationListItem component
// TODO: Add socket listener for real-time updates

interface ConversationListProps {
    selectedConversationId: number | null;
    onSelectConversation: (conversationId: number) => void;
    initialTargetUserId?: number | null;
    initialTargetUserName?: string | null;
}

const ConversationList: React.FC<ConversationListProps> = ({ 
    selectedConversationId, 
    onSelectConversation, 
    initialTargetUserId, 
    initialTargetUserName 
}) => {
    const { user: currentUser } = useAuth();
    
    // Fetch conversations using useQuery
    const { 
        data: conversations = [],
        isLoading,
        error,
        isSuccess
    } = useQuery<ConversationListItem[], Error>(
        ['conversations', currentUser?.id], 
        getConversations, 
        {
            enabled: !!currentUser?.id,
            refetchOnWindowFocus: false,
        }
    );

    // Effect to find and select conversation based on initialTargetUserId
    useEffect(() => {
        if (initialTargetUserId && isSuccess && conversations.length > 0 && !selectedConversationId) {
            let foundConversation = false;
            for (const conv of conversations) {
                if (conv.participants?.some(participant => participant.id === initialTargetUserId)) {
                    console.log(`Found existing conversation (${conv.id}) with user ID ${initialTargetUserId}`);
                    onSelectConversation(conv.id);
                    foundConversation = true;
                    break;
                }
            }

            if (!foundConversation) {
                console.log(`No existing conversation found with user ID ${initialTargetUserId}. User Name: ${initialTargetUserName}`);
                showToast(`Start a new conversation with ${initialTargetUserName || 'this user'}.`, 'info');
            }
        }
    }, [conversations, initialTargetUserId, initialTargetUserName, onSelectConversation, isSuccess, selectedConversationId]);

    if (!currentUser?.id) {
        return <div className="p-3 text-center text-muted">Loading user...</div>;
    }

    if (isLoading) {
        return <LoadingSpinner text="Loading conversations..." />;
    }

    if (error) {
        // Provide a default error message
        return <ErrorAlert message={error?.message || 'An unexpected error occurred while loading conversations.'} />;
    }

    return (
        <ListGroup variant="flush" className="conversation-list">
            {conversations.length > 0 ? (
                conversations.map((conv) => (
                    <ListGroup.Item 
                        key={conv.id} 
                        action 
                        active={selectedConversationId === conv.id}
                        onClick={() => onSelectConversation(conv.id)}
                        className="conversation-list-item border-bottom px-3 py-2"
                    >
                        {/* Basic display - Enhance with ConversationListItem component later */}
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            {/* Display participant name(s) - assumes 1-on-1 and participants array has the other user */}
                            <span className="fw-bold text-truncate me-2">
                                {/* Check if participants array exists and has a first element */}
                                {conv.participants && conv.participants.length > 0 ? 
                                 conv.participants[0]?.name || 'Unknown User' : 
                                 'Unknown User'} 
                            </span>
                            <small className="text-muted text-nowrap">
                                {conv.lastMessage ? new Date(conv.lastMessage.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                            </small>
                        </div>
                        <p className="mb-0 text-muted text-truncate small">
                            {/* Optionally show sender for last message */}
                            {/* {conv.lastMessage?.senderId === currentUser?.id ? 'You: ' : ''} */}
                            {conv.lastMessage?.content || 'No messages yet'}
                        </p>
                    </ListGroup.Item>
                ))
            ) : (
                <div className="p-3 text-center text-muted">
                    No conversations found.
                </div>
            )}
        </ListGroup>
    );
};

export default ConversationList; 